import { useEffect, useRef, useState } from "react";
import {
  init,
  dispose,
  type Chart,
  type KLineData,
} from "klinecharts";
import type { BarOut } from "../api/bars";
import { getVolumeProfile, getWeeklyVolumeProfiles } from "../api/bars";
import type { BacktestTrade } from "../api/backtests";
import {
  registerCustomIndicators,
  setGoldenMap,
  setValueAreaMap,
} from "../lib/klineIndicators";
import { registerCustomOverlays } from "../lib/klineOverlays";
import { parseTs } from "../lib/time";
import type { IndicatorInstance, IndicatorType } from "../lib/indicators";
import DrawingToolbar from "./DrawingToolbar";

// Defensive: if anything in our custom-indicator registration throws, we
// don't want to take the whole router (which eagerly imports all pages,
// including this one) down with us. Catch + log instead of letting the
// error propagate out of module evaluation.
try {
  registerCustomIndicators();
  registerCustomOverlays();
} catch (e) {
  // eslint-disable-next-line no-console
  console.error("klinecharts custom registration failed:", e);
}

interface Props {
  bars: BarOut[];
  trades: BacktestTrade[];
  indicators: IndicatorInstance[];
  /** Callback fired when the user edits an indicator's params/styling via
   *  the chip-click popover. Parent updates its IndicatorInstance state. */
  onIndicatorChange?: (id: string, patch: Partial<IndicatorInstance>) => void;
  /** When set, PriceChart opens the indicator edit popover for that
   *  instance. Cleared by the parent (via onCloseIndicatorEdit) when the
   *  user applies or cancels. */
  editingIndicator?: IndicatorInstance | null;
  /** Called when the indicator edit popover closes (apply or cancel). */
  onCloseIndicatorEdit?: () => void;
  /** Top-left instrument label, e.g. "DELTA:BTCUSD". */
  watermark?: string;
  /** Timeframe / candle duration, e.g. "1h", "5m". Rendered as a prominent
   *  green badge so the user can always see what the candles represent. */
  timeframe?: string;
  /** Instrument metadata so the volume-profile overlay can ask the backend
   *  to compute the profile from the finest available timeframe (the chart
   *  may be at 1h while we have 1m bars in DuckDB — backend uses the 1m). */
  instrument?: {
    symbol: string;
    exchange: string;
    instrument_type?: string;
    expiry?: string | null;
    strike?: string | null;
    lot_size?: number;
    tick_size?: string;
  };
  /**
   * Tailwind height class for the chart container. Defaults to h-[480px].
   * Pass `h-full` when the parent is providing a sized wrapper.
   */
  heightClass?: string;
  /** Per-day golden numbers (IST day "YYYY-MM-DD" -> level). When present, a
   *  GOLDEN step-line is drawn on the candle pane — one horizontal level per
   *  session. Used by the Golden Number Breakout backtest chart. */
  goldenByDay?: Record<string, number>;
  /** Golden Number Breakout ladder offsets (points from the golden number).
   *  When set alongside goldenByDay, golden ± each offset is drawn as dotted
   *  step-lines (both sides). */
  ladderPoints?: number[];
  /** Per-IST-day value-area bounds. When set, VAH/VAL are drawn as step lines;
   *  with `valueAreaLadder` the rungs running inward from them are drawn too
   *  (call = VAL + offset, put = VAH - offset). */
  valueAreaByDay?: Record<string, { vah: number; val: number }>;
  /** Draw the ladder rungs off the value-area edges instead of the golden
   *  number — mirrors the strategy's `ladders_on_value_area`. */
  valueAreaLadder?: boolean;
  /** Live tail bars (Chart page). Pushed through klinecharts' subscribeBar
   *  channel so the last candle updates in place WITHOUT re-applying the
   *  whole dataset (which would reset the user's scroll/zoom). A bar whose
   *  timestamp matches the current last bar patches it; a newer one appends. */
  liveBars?: BarOut[];
  /** Lazy history loader (Chart page). When the user scrolls left past the
   *  loaded range, klinecharts asks for older bars (its DataLoader calls
   *  this a 'forward' load); this fetches the chunk ending just before
   *  `beforeTsIso`. Return [] when no older data exists — that stops
   *  further history requests. */
  onLoadMoreHistory?: (beforeTsIso: string) => Promise<BarOut[]>;
}

/** Map our IndicatorType -> registered klinecharts indicator name. */
const KLINE_NAME_BY_TYPE: Record<IndicatorType, string> = {
  sma: "SMA",
  ema: "EMA",
  wma: "WMA",
  bollinger: "BOLL",
  donchian: "DONCH",
  rsi: "RSI",
  macd: "MACD",
  stochastic: "STOCH",
  atr: "ATR",
  roc: "ROC",
  supertrend: "SUPERTREND",
  vwap: "VWAP",
};

/** Map our IndicatorInstance.params -> klinecharts calcParams order. */
function calcParamsFor(inst: IndicatorInstance): number[] {
  switch (inst.type) {
    case "bollinger":
      return [inst.params.period, inst.params.std_dev];
    case "macd":
      return [
        inst.params.fast_period,
        inst.params.slow_period,
        inst.params.signal_period,
      ];
    case "stochastic":
      return [inst.params.k_period, inst.params.d_period];
    case "supertrend":
      return [inst.params.atr_period, inst.params.multiplier];
    case "vwap":
      return [inst.params.deviation_pct ?? 0];
    default:
      // Single-period indicators: sma/ema/wma/donchian/rsi/atr/roc.
      return [inst.params.period];
  }
}

/** Grid lines for the current theme: near-white lines vanish into a light
 *  canvas as intended but glare on the dark one. */
function gridStyles() {
  const color = document.documentElement.classList.contains("dark")
    ? "#1e293b" // slate-800
    : "#f1f5f9"; // slate-100
  return { horizontal: { color }, vertical: { color } };
}

export default function PriceChart({
  bars,
  trades,
  indicators,
  onIndicatorChange,
  editingIndicator,
  onCloseIndicatorEdit,
  watermark,
  timeframe,
  instrument,
  heightClass,
  liveBars,
  onLoadMoreHistory,
  goldenByDay,
  ladderPoints,
  valueAreaByDay,
  valueAreaLadder,
}: Props) {
  // Keep the latest instrument prop in a ref so the async fetch handlers
  // installed when an overlay is created always see the current value even
  // if the prop changes between create and fetch.
  const instrumentRef = useRef(instrument);
  instrumentRef.current = instrument;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  // Indicator ids returned from createIndicator, so we can remove them
  // cleanly when the indicators[] prop changes.
  const indicatorIdsRef = useRef<string[]>([]);
  // Trade-marker overlay ids — same idea.
  const tradeOverlayIdsRef = useRef<string[]>([]);
  // Stable volume-pane indicator id (we always show one — TradingView-style
  // volume bars below the candle pane).
  const volIndicatorIdRef = useRef<string | null>(null);
  // Golden-number step-line indicator id (candle pane; created only when
  // goldenByDay is provided).
  const goldenIndicatorIdRef = useRef<string | null>(null);
  // Golden ladder-levels (dotted) indicator id.
  const ladderIndicatorIdRef = useRef<string | null>(null);
  const vaIndicatorIdRef = useRef<string | null>(null);
  const vaLadderIndicatorIdRef = useRef<string | null>(null);
  // Push callback for live tail bars, handed to us by klinecharts'
  // DataLoader.subscribeBar after the initial getBars completes.
  const liveCallbackRef = useRef<((d: KLineData) => void) | null>(null);
  // Latest lazy-history loader in a ref so the DataLoader (installed by the
  // dataset effect) always calls the current one without being an effect dep.
  const loadMoreRef = useRef(onLoadMoreHistory);
  loadMoreRef.current = onLoadMoreHistory;
  // ID of the user-drawn overlay currently "selected" by click. Press Delete
  // or Backspace to remove just that one and leave the rest of the chart's
  // drawings intact. Cleared when the user clicks an overlay other than the
  // last-clicked, or removes the current selection.
  const selectedOverlayIdRef = useRef<string | null>(null);
  // Volume-profile parameter editor state — appears as a popover when
  // the user double-clicks (or right-click → Edit) a placed volume-
  // profile overlay. `kind` discriminates the manual `volumeProfile`
  // (which exposes numLevels + valueAreaPct) from the periodic
  // `weeklyVolumeProfile` (numLevels + period). The popover shows
  // different inputs based on this flag.
  const [vpEdit, setVpEdit] = useState<{
    overlayId: string;
    kind: "volumeProfile" | "weeklyVolumeProfile";
    x: number;
    y: number;
    numLevels: number;
    valueAreaPct: number;
    period: "daily" | "weekly" | "monthly" | "quarterly";
    periodCount: number;
    developing: boolean;
  } | null>(null);
  // Right-click context menu state. Klinecharts v10's default right-click
  // *removes* the overlay outright; we suppress that with preventDefault()
  // and show a tiny menu instead so the user has to explicitly pick Remove.
  // overlayName lets the menu show overlay-specific actions (e.g. "Edit
  // profile…" only on volumeProfile overlays).
  const [contextMenu, setContextMenu] = useState<{
    overlayId: string;
    overlayName: string;
    x: number;
    y: number;
  } | null>(null);
  // "Go to date" picker open/closed.
  const [jumpOpen, setJumpOpen] = useState(false);
  // Whether BUY/SELL trade markers are shown. Users hide them to study the
  // raw price action, then unhide to see where the strategy traded.
  const [showTrades, setShowTrades] = useState(true);
  // Earliest + latest bar timestamps, used to bound the jump picker.
  const barRangeRef = useRef<{ min: number; max: number } | null>(null);
  // When set, the candle pane's y-axis is overridden to a range centered on
  // this price (and reverts to auto-fit when null). Used by jumpTo to put
  // the target bar's close at the vertical middle of the screen. Cleared
  // shortly after the jump animation finishes so subsequent scrolling
  // resumes klinecharts' default auto-y behavior.
  const centerPriceRef = useRef<number | null>(null);
  // Indicator edit popover (params + color + line width) — appears when the
  // user clicks an indicator chip in the IndicatorsPanel.
  const [indicatorEdit, setIndicatorEdit] = useState<{
    klineId: string; // klinecharts indicator id (for overrideIndicator)
    instanceId: string; // our IndicatorInstance id (for the parent state)
    type: import("../lib/indicators").IndicatorType;
    params: Record<string, number>;
    color: string;
    lineWidth: number;
    label: string;
  } | null>(null);
  // Map our IndicatorInstance.id -> the klinecharts indicator id returned
  // by createIndicator. We need both because the edit popover needs to
  // call overrideIndicator(klineId) while updates flow back to the parent
  // by IndicatorInstance.id.
  const klineIdByInstanceRef = useRef<Map<string, string>>(new Map());

  // 1. Init the chart once.
  useEffect(() => {
    if (!containerRef.current) return;
    const chart = init(containerRef.current, {
      styles: {
        grid: gridStyles(),
        candle: {
          bar: {
            upColor: "#059669",
            downColor: "#dc2626",
            upBorderColor: "#059669",
            downBorderColor: "#dc2626",
            upWickColor: "#059669",
            downWickColor: "#dc2626",
          },
          tooltip: {
            showRule: "follow_cross",
            // Push the OHLCV legend below the overlay controls row (symbol
            // watermark + timeframe badge + "Go to date" button sit at
            // top-3 ≈ 12px and are ~30px tall) so they never cover it.
            offsetTop: 48,
            legend: {
              // Custom legend: Time / O / H / L / C / Volume / Chg%.
              // Chg% compares close to the previous bar's close (TradingView
              // convention); falls back to open→close on the first bar.
              template: ({ prev, current }: {
                prev: KLineData | null;
                current: KLineData | null;
              }) => {
                if (!current) return [];
                const base = prev?.close ?? current.open;
                const chg = base !== 0 ? ((current.close - base) / base) * 100 : 0;
                const chgColor =
                  chg > 0 ? "#059669" : chg < 0 ? "#dc2626" : "#64748b";
                const dow = new Date(current.timestamp).toLocaleDateString(
                  "en-US",
                  { weekday: "short" },
                );
                const when = new Date(current.timestamp).toLocaleString("en-IN", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                  hour12: false,
                });
                return [
                  { title: "Time: ", value: `${dow} ${when}` },
                  { title: "O: ", value: fmtPrice(current.open) },
                  { title: "H: ", value: fmtPrice(current.high) },
                  { title: "L: ", value: fmtPrice(current.low) },
                  { title: "C: ", value: fmtPrice(current.close) },
                  { title: "Vol: ", value: fmtVolume(current.volume ?? 0) },
                  {
                    title: "Chg: ",
                    value: {
                      text: `${chg > 0 ? "+" : ""}${chg.toFixed(2)}%`,
                      color: chgColor,
                    },
                  },
                ];
              },
            },
          },
        },
        crosshair: { show: true },
      },
    });
    if (!chart) return;
    chartRef.current = chart;
    // Follow a theme switch while the chart is open: the grid is the one
    // colour here that only reads on a light canvas (near-white lines).
    const themeWatch = new MutationObserver(() =>
      chart.setStyles({ grid: gridStyles() }),
    );
    themeWatch.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["class"],
    });
    // Prefix the day-of-week onto the crosshair + tooltip date so the user
    // can eyeball at-a-glance whether a trade landed on Mon/Tue/etc. — handy
    // for the weekly_htp_revert strategy where every entry should land on
    // the first trading day of an ISO week. We leave the static x-axis
    // ticks untouched (a "Mon" prefix on every tick would just clutter).
    chart.setFormatter({
      formatDate: ({ dateTimeFormat, timestamp, type }) => {
        const base = dateTimeFormat.format(new Date(timestamp));
        if (type === "crosshair" || type === "tooltip") {
          const dow = new Date(timestamp).toLocaleDateString("en-US", {
            weekday: "short",
          });
          return `${dow} ${base}`;
        }
        return base;
      },
    });
    // Install a one-shot y-axis override on the candle pane: while
    // centerPriceRef is set, the visible price range stays centered on that
    // price (so a jumpTo lands with the target bar at the vertical middle).
    // When the ref is null we return defaultRange and klinecharts' normal
    // auto-fit-to-visible-bars takes over. The cast is because klinecharts'
    // .d.ts swaps the X/Y override types — the runtime accepts a
    // YAxisOverride (paneId + createRange) here despite the typing.
    (
      chart as unknown as {
        overrideYAxis: (opts: {
          paneId: string;
          createRange: (params: {
            defaultRange: {
              from: number;
              to: number;
              realFrom: number;
              realTo: number;
              range: number;
              realRange: number;
              displayFrom: number;
              displayTo: number;
              displayRange: number;
            };
          }) => {
            from: number;
            to: number;
            realFrom: number;
            realTo: number;
            range: number;
            realRange: number;
            displayFrom: number;
            displayTo: number;
            displayRange: number;
          };
        }) => void;
      }
    ).overrideYAxis({
      paneId: "candle_pane",
      createRange: ({ defaultRange }) => {
        const target = centerPriceRef.current;
        if (target == null) return defaultRange;
        const half = defaultRange.range / 2;
        const from = target - half;
        const to = target + half;
        return {
          ...defaultRange,
          from,
          to,
          realFrom: from,
          realTo: to,
          displayFrom: from,
          displayTo: to,
        };
      },
    });
    return () => {
      themeWatch.disconnect();
      if (containerRef.current) dispose(containerRef.current);
      chartRef.current = null;
      indicatorIdsRef.current = [];
      tradeOverlayIdsRef.current = [];
      volIndicatorIdRef.current = null;
      goldenIndicatorIdRef.current = null;
      ladderIndicatorIdRef.current = null;
    };
  }, []);

  // 2. Push bars whenever they change. Use setDataLoader (v10's preferred
  //    API): klinecharts calls getBars and we hand it the bars we already
  //    have. We set a synthetic symbol+period so the chart's tooltip header
  //    shows our exchange:symbol and timeframe.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const dataList: KLineData[] = bars
      .map((b) => ({
        timestamp: parseTs(b.ts),
        open: Number(b.open),
        high: Number(b.high),
        low: Number(b.low),
        close: Number(b.close),
        volume: Number(b.volume) || 0,
      }))
      .filter((b) => Number.isFinite(b.timestamp) && Number.isFinite(b.open))
      .sort((a, b) => a.timestamp - b.timestamp);

    chart.setSymbol({ ticker: watermark ?? "" });
    chart.setPeriod(periodFromString(timeframe));
    chart.setDataLoader({
      getBars: async ({ type, timestamp, callback }) => {
        // klinecharts v10 direction naming is counter-intuitive:
        //   'forward'  = user scrolled to the OLDEST loaded bar; response is
        //                PREPENDED (timestamp = first bar's ts) → history.
        //   'backward' = user is at the NEWEST bar; response is APPENDED
        //                (timestamp = last bar's ts) → newer data.
        // Newer data only ever arrives via subscribeBar (the live tail), so
        // 'backward' always answers empty — answering it with anything else
        // glues stale bars after the live candle.
        if (type === "backward") {
          callback([], { backward: false, forward: false });
          return;
        }
        if (type === "forward") {
          const loadMore = loadMoreRef.current;
          if (!loadMore || timestamp == null) {
            callback([], { backward: false, forward: false });
            return;
          }
          try {
            const older = await loadMore(new Date(timestamp).toISOString());
            const data: KLineData[] = older
              .map((b) => ({
                timestamp: parseTs(b.ts),
                open: Number(b.open),
                high: Number(b.high),
                low: Number(b.low),
                close: Number(b.close),
                volume: Number(b.volume) || 0,
              }))
              .filter(
                (b) => Number.isFinite(b.timestamp) && Number.isFinite(b.open),
              )
              .sort((a, b) => a.timestamp - b.timestamp);
            if (data.length > 0 && barRangeRef.current) {
              barRangeRef.current.min = Math.min(
                barRangeRef.current.min,
                data[0].timestamp,
              );
            }
            callback(data, { forward: data.length > 0, backward: false });
            // Periodic volume profiles were computed over the previously
            // loaded range — older buckets have no data until we re-ask the
            // server with the widened window. Debounced: scroll-back loads
            // several chunks in quick succession.
            if (data.length > 0) schedulePeriodicOverlayRefresh();
          } catch {
            callback([], { backward: false, forward: false });
          }
          return;
        }
        // init / update: the dataset from the parent. History available on
        // scroll-left when a loader exists; newer data only via subscribeBar.
        callback(dataList, { forward: !!loadMoreRef.current, backward: false });
      },
      // Live channel: klinecharts hands us a push callback after the initial
      // load; the liveBars effect below feeds tail bars through it so the
      // last candle updates in place without resetting scroll/zoom.
      subscribeBar: ({ callback }) => {
        liveCallbackRef.current = callback;
      },
      unsubscribeBar: () => {
        liveCallbackRef.current = null;
      },
    });
    // Track the bar timestamp range so the Jump-to-date picker can clamp
    // its min/max attrs and we can avoid scrolling to an out-of-range
    // moment by mistake.
    if (dataList.length > 0) {
      barRangeRef.current = {
        min: dataList[0].timestamp,
        max: dataList[dataList.length - 1].timestamp,
      };
    } else {
      barRangeRef.current = null;
    }
  }, [bars, watermark, timeframe]);

  // Push live tail bars through the subscribeBar channel. klinecharts
  // patches the last candle in place when the timestamp matches, appends
  // when newer, and ignores older ones.
  //
  // Timestamps must first be snapped onto the chart's bucket grid: Kite
  // stamps the IN-PROGRESS candle with its last-update time (seconds
  // precision, drifting forward every poll), so pushing it raw makes
  // klinecharts append a brand-new candle every 5 seconds instead of
  // updating the live one. We anchor on the chart's last bar (always a
  // clean bucket boundary from the store) and floor each pushed timestamp
  // to `lastBar + n * barDuration` — n=0 updates the live candle, n>=1
  // starts the next one. Anchoring on the last bar (rather than midnight)
  // keeps NSE's 9:15-anchored hourly grid and overnight session gaps
  // correct, and is a no-op for already-aligned sources (Dukascopy/Delta).
  useEffect(() => {
    const push = liveCallbackRef.current;
    if (!push || !liveBars || liveBars.length === 0) return;
    const durMs = timeframeToMs(timeframe);
    const sorted = liveBars
      .map((b) => ({
        timestamp: parseTs(b.ts),
        open: Number(b.open),
        high: Number(b.high),
        low: Number(b.low),
        close: Number(b.close),
        volume: Number(b.volume) || 0,
      }))
      .filter((b) => Number.isFinite(b.timestamp) && Number.isFinite(b.open))
      .sort((a, b) => a.timestamp - b.timestamp);
    for (const k of sorted) {
      const anchor = barRangeRef.current?.max;
      if (anchor !== undefined && k.timestamp > anchor && durMs > 0) {
        const buckets = Math.floor((k.timestamp - anchor) / durMs);
        k.timestamp = anchor + buckets * durMs;
      }
      push(k);
      if (barRangeRef.current && k.timestamp > barRangeRef.current.max) {
        barRangeRef.current.max = k.timestamp;
      }
    }
  }, [liveBars, timeframe]);

  // Jump-to-date helper: scroll the time axis so the picked timestamp is
  // centered. klinecharts' scrollToTimestamp accepts ms epoch and snaps to
  // the closest bar.
  // Open the indicator-edit popover whenever the parent passes a non-null
  // editingIndicator. Reads the current params off the IndicatorInstance.
  useEffect(() => {
    if (!editingIndicator) {
      setIndicatorEdit(null);
      return;
    }
    const klineId = klineIdByInstanceRef.current.get(editingIndicator.id);
    if (!klineId) return;
    setIndicatorEdit({
      klineId,
      instanceId: editingIndicator.id,
      type: editingIndicator.type,
      params: { ...editingIndicator.params },
      color: editingIndicator.color ?? "#0ea5e9",
      lineWidth: editingIndicator.lineWidth ?? 1,
      label: editingIndicator.label,
    });
  }, [editingIndicator]);

  function jumpTo(ts: number, animationDuration = 300) {
    const chart = chartRef.current;
    const range = barRangeRef.current;
    if (!chart || !range) return;
    const clamped = Math.min(Math.max(ts, range.min), range.max);
    const data = chart.getDataList();
    if (data.length === 0) {
      setJumpOpen(false);
      return;
    }
    // Binary-search the nearest bar to the picked timestamp.
    let lo = 0;
    let hi = data.length - 1;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (data[mid].timestamp < clamped) lo = mid + 1;
      else hi = mid;
    }
    if (
      lo > 0 &&
      Math.abs(data[lo - 1].timestamp - clamped) <
        Math.abs(data[lo].timestamp - clamped)
    ) {
      lo -= 1;
    }
    const targetIdx = lo;
    const targetBar = data[targetIdx];
    // klinecharts' scrollToTimestamp/scrollToDataIndex places the target at
    // the right edge of the visible window. We want it horizontally centered
    // so bars before AND after the target are visible. Compute the extra
    // shift that pushes the target from right-edge to middle:
    //   currentLastBarRightSideDiffBarCount = visibleRange.to - dataList.length
    //   desired (after centering on targetIdx) = targetIdx + halfVisible - lastIdx
    //   distance = (current - desired) * barSpace
    const visible = chart.getVisibleRange();
    const visibleBars = Math.max(1, visible.to - visible.from);
    const halfVisible = Math.floor(visibleBars / 2);
    const lastIdx = data.length - 1;
    const currentRightDiff = visible.to - data.length;
    const desiredRightDiff = targetIdx + halfVisible - lastIdx;
    const barSpace = chart.getBarSpace().bar;
    const distance = (currentRightDiff - desiredRightDiff) * barSpace;
    // Vertically center on the target bar's close. Cleared after the
    // animation so the user can scroll/zoom normally afterward.
    centerPriceRef.current = targetBar.close;
    chart.scrollByDistance(distance, animationDuration);
    setJumpOpen(false);
    window.setTimeout(() => {
      centerPriceRef.current = null;
    }, animationDuration + 150);
  }

  // 3. Volume pane, created once below the candle pane so that top-to-bottom
  //    the chart reads: candles -> volume. TradingView uses the same stacking.
  //    The VOL indicator is klinecharts' built-in one — it renders green/red
  //    bars matching the candle direction; bars with volume=0 (e.g. raw NSE
  //    index data without the synthetic-volume flag) just render as empty
  //    space.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    if (!volIndicatorIdRef.current) {
      // calcParams: [] disables the built-in MA5/10/20 of volume that
      // klinecharts' VOL indicator paints on top of the bars. We want a
      // clean volume pane like TradingView's default; users who want a
      // smoothed-volume line can add an EMA from the Indicators panel.
      const volId = chart.createIndicator(
        { name: "VOL", calcParams: [] },
        {
          isStack: false,
          pane: { id: "vol_pane", height: 80 },
        },
      );
      if (volId) volIndicatorIdRef.current = volId;
    }
  }, []);

  // Golden-number step line (candle pane) — the daily golden number for the
  // Golden Number Breakout backtest. We push the day->value map before
  // (re)creating the GOLDEN indicator so its calc reads fresh values; the
  // indicator is removed when no golden data is present.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    const entries = goldenByDay ? Object.entries(goldenByDay) : [];
    setGoldenMap(entries);
    const vaEntries = valueAreaByDay ? Object.entries(valueAreaByDay) : [];
    setValueAreaMap(vaEntries);
    for (const ref of [
      goldenIndicatorIdRef,
      ladderIndicatorIdRef,
      vaIndicatorIdRef,
      vaLadderIndicatorIdRef,
    ]) {
      if (ref.current) {
        chart.removeIndicator({ id: ref.current });
        ref.current = null;
      }
    }
    // Value area first, so the golden line paints over it rather than under.
    if (vaEntries.length > 0) {
      const vaId = chart.createIndicator(
        {
          name: "VALUEAREA",
          styles: {
            lines: [
              { color: "#38bdf8", size: 1 },
              { color: "#38bdf8", size: 1 },
            ],
          },
        },
        { isStack: true, pane: { id: "candle_pane" } },
      );
      if (vaId) vaIndicatorIdRef.current = vaId;

      const vaOffs = (ladderPoints ?? []).filter(
        (n) => Number.isFinite(n) && n > 0,
      );
      if (valueAreaLadder && vaOffs.length > 0) {
        const LADDER_MAX = 6;
        const vaLadderId = chart.createIndicator(
          {
            name: "VALADDER",
            calcParams: Array.from(
              { length: LADDER_MAX },
              (_, i) => vaOffs[i] ?? 0,
            ),
            styles: {
              lines: Array.from({ length: LADDER_MAX * 2 }, () => ({
                color: "#38bdf8",
                size: 1,
                style: "dashed" as const,
                dashedValue: [2, 3],
              })),
            },
          },
          { isStack: true, pane: { id: "candle_pane" } },
        );
        if (vaLadderId) vaLadderIndicatorIdRef.current = vaLadderId;
      }
    }

    if (entries.length === 0) return;
    const id = chart.createIndicator(
      { name: "GOLDEN", styles: { lines: [{ color: "#eab308", size: 2 }] } },
      { isStack: true, pane: { id: "candle_pane" } },
    );
    if (id) goldenIndicatorIdRef.current = id;

    // Ladder levels (golden ± offsets, both sides) as dotted lines.
    const offs = (ladderPoints ?? []).filter((n) => Number.isFinite(n) && n > 0);
    if (offs.length > 0) {
      const dotted = {
        color: "#94a3b8",
        size: 1,
        style: "dashed" as const,
        dashedValue: [2, 3],
      };
      // GLADDER draws up to 6 ladder levels (12 lines); pad calcParams to 6.
      const LADDER_MAX = 6;
      const calcParams = Array.from(
        { length: LADDER_MAX },
        (_, i) => offs[i] ?? 0,
      );
      const ladderId = chart.createIndicator(
        {
          name: "GLADDER",
          calcParams,
          styles: {
            lines: Array.from({ length: LADDER_MAX * 2 }, () => ({ ...dotted })),
          },
        },
        { isStack: true, pane: { id: "candle_pane" } },
      );
      if (ladderId) ladderIndicatorIdRef.current = ladderId;
    }
  }, [goldenByDay, ladderPoints, valueAreaByDay, valueAreaLadder, bars]);

  // 4. (Re)render indicators when indicators[] changes.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    // Remove existing ones we own.
    for (const id of indicatorIdsRef.current) {
      chart.removeIndicator({ id });
    }
    indicatorIdsRef.current = [];

    for (const inst of indicators) {
      const klineName = KLINE_NAME_BY_TYPE[inst.type];
      // For "price"-pane indicators, target the built-in candle pane
      // ("candle_pane") explicitly. `isStack: true` alone stacks on the
      // *most recently created* pane, which means once we create the volume
      // pane during init, subsequent SMAs/EMAs land on volume instead of
      // candles. The fix is to name the candle pane directly so klinecharts
      // routes the indicator there regardless of pane creation order.
      //
      // Below-pane indicators share a single oscillator pane ("osc_pane") so
      // adding three RSIs doesn't fragment the layout.
      const targetPaneId = inst.pane === "price" ? "candle_pane" : "osc_pane";
      const lineWidth = inst.lineWidth ?? 1;
      const id = chart.createIndicator(
        {
          name: klineName,
          shortName: inst.label,
          calcParams: calcParamsFor(inst),
          // NEVER pass `bars: []` — klinecharts indexes bar styles with
          // `barStyles[i % barStyles.length]`, so an empty array yields
          // `barStyles[NaN] = undefined` and the figure loop throws while
          // styling any bar figure. That silently blanked the entire MACD
          // (its histogram is our only oscillator bar figure). Omit the key
          // instead so klinecharts falls back to its default bar styles.
          styles: {
            ...(inst.color
              ? {
                  bars: [
                    {
                      upColor: inst.color,
                      downColor: inst.color,
                      noChangeColor: inst.color,
                    },
                  ],
                }
              : {}),
            lines: inst.color
              ? [{ color: inst.color, size: lineWidth }]
              : [{ size: lineWidth }],
          },
        },
        {
          isStack: true,
          pane:
            targetPaneId === "candle_pane"
              ? { id: "candle_pane" }
              : { id: "osc_pane", height: 120 },
        },
      );
      if (id) {
        indicatorIdsRef.current.push(id);
        klineIdByInstanceRef.current.set(inst.id, id);
      }
    }
  }, [indicators]);

  // Apply edits from the indicator-edit popover. Pushes new calcParams +
  // styles into klinecharts via overrideIndicator, then bubbles the change
  // to the parent so its IndicatorInstance state stays in sync.
  function applyIndicatorEdit() {
    const edit = indicatorEdit;
    const chart = chartRef.current;
    if (!edit || !chart) return;
    const params = edit.params;
    const calcParams = (() => {
      switch (edit.type) {
        case "bollinger":
          return [params.period, params.std_dev];
        case "macd":
          return [
            params.fast_period,
            params.slow_period,
            params.signal_period,
          ];
        case "stochastic":
          return [params.k_period, params.d_period];
        case "supertrend":
          return [params.atr_period, params.multiplier];
        case "vwap":
          return [params.deviation_pct ?? 0];
        default:
          return [params.period];
      }
    })();
    chart.overrideIndicator({
      id: edit.klineId,
      name: KLINE_NAME_BY_TYPE[edit.type],
      calcParams,
      styles: {
        bars: [{ upColor: edit.color, downColor: edit.color, noChangeColor: edit.color }],
        lines: [{ color: edit.color, size: edit.lineWidth }],
      },
    });
    onIndicatorChange?.(edit.instanceId, {
      params,
      color: edit.color,
      lineWidth: edit.lineWidth,
      label: edit.label,
    });
    setIndicatorEdit(null);
    onCloseIndicatorEdit?.();
  }

  function cancelIndicatorEdit() {
    setIndicatorEdit(null);
    onCloseIndicatorEdit?.();
  }

  // 5. Trade markers — render each fill as a small "simpleAnnotation" overlay
  //    on the candle pane. Re-render whenever trades OR bars change (bars
  //    triggers re-render so markers land after data is in).
  //
  //    UX rules:
  //    - BUY  → marker sits *below* the bar's low (just under the candle)
  //    - SELL → marker sits *above* the bar's high (just over the candle)
  //      so they never overlap regardless of direction at the same bar.
  //    - A fill with a non-zero realized_pnl_delta is a *close* trade
  //      (closing-out a prior position). For closes we append a second
  //      line "P/L: ±amount" and tint that line green/red by sign — at-
  //      a-glance signal of whether the close was profit or loss.
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) return;
    for (const id of tradeOverlayIdsRef.current) {
      chart.removeOverlay({ id });
    }
    tradeOverlayIdsRef.current = [];
    // Trade markers hidden — leave the overlays cleared. Toggling showTrades
    // re-runs this effect and rebuilds them.
    if (!showTrades) return;
    if (bars.length === 0) return;

    // Bar lookup for marker positioning: fills can now carry the intrabar
    // *touch minute* (option-routed engine reprices at 1m), so a fill ts is
    // anchored to the containing bar — the greatest bar ts <= fill ts — not
    // an exact match. Bars arrive chronological; binary search.
    const barTsArr = bars.map((b) => parseTs(b.ts));
    const floorBarIdx = (ts: number): number => {
      let lo = 0;
      let hi = barTsArr.length - 1;
      let ans = -1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (barTsArr[mid] <= ts) {
          ans = mid;
          lo = mid + 1;
        } else {
          hi = mid - 1;
        }
      }
      return ans;
    };

    // Collision avoidance: markers whose anchors are close together stack at
    // the same height and their labels overlap. We assign each marker a
    // vertical "lane" — the overlay lengthens its leader line by the lane so
    // the label clears the ones before it. Lanes are tracked separately per
    // direction (BUY markers below the candle, SELL above) since those two
    // groups never collide with each other. Two markers within
    // STACK_WINDOW_BARS of each other (in the same direction) get consecutive
    // lanes; a larger gap resets to lane 0. Lanes cycle at MAX_LANES so a very
    // dense run doesn't push a label off-screen (rare; only re-overlaps past
    // MAX_LANES consecutive same-side fills within the window).
    const STACK_WINDOW_BARS = 8;
    const MAX_LANES = 6;
    const lastByDir: Record<
      "up" | "down",
      { barIdx: number; lane: number } | null
    > = { up: null, down: null };

    // Fills arrive chronologically, but sort defensively so lane assignment is
    // always left-to-right.
    const sortedTrades = [...trades].sort(
      (a, b) => parseTs(a.ts) - parseTs(b.ts),
    );

    const newIds: string[] = [];
    for (const t of sortedTrades) {
      const ts = parseTs(t.ts);
      if (!Number.isFinite(ts)) continue;
      const isBuy = t.side.toUpperCase() === "BUY";
      const pnl = Number(t.realized_pnl_delta);
      const isClose = Number.isFinite(pnl) && pnl !== 0;

      // Lane for this marker (see comment above).
      const dir: "up" | "down" = isBuy ? "down" : "up";
      const barIdx = floorBarIdx(ts);
      const last = lastByDir[dir];
      let lane = 0;
      if (
        last &&
        barIdx >= 0 &&
        last.barIdx >= 0 &&
        barIdx - last.barIdx < STACK_WINDOW_BARS
      ) {
        lane = (last.lane + 1) % MAX_LANES;
      }
      lastByDir[dir] = { barIdx: barIdx >= 0 ? barIdx : last?.barIdx ?? 0, lane };

      const bar = barIdx >= 0 ? bars[barIdx] : undefined;
      // Anchor exactly at the candle's high/low — the custom tradeMarker
      // overlay adds its own ~56px of line+arrow+label past that anchor,
      // so the visible separation comes for free. Fall back to fill
      // price if the bar isn't on the chart (e.g. downsampled view).
      // For option-routed runs the fill price is a PREMIUM (~100s) while
      // the chart shows the base (~24000s) — anchoring to the containing
      // base candle is what keeps those markers on scale.
      const yValue = bar
        ? isBuy
          ? Number(bar.low)
          : Number(bar.high)
        : Number(t.price);
      const anchorTs = bar ? barTsArr[barIdx] : ts;

      // Keep the marker compact: price to 2 dp, quantity without trailing
      // zeros (the raw backend strings carry many decimals, e.g. "22050.5000"
      // / "1.00000000", which made the labels very long).
      const priceNum = Number(t.price);
      const priceText = Number.isFinite(priceNum) ? priceNum.toFixed(2) : t.price;
      const qtyNum = Number(t.quantity);
      const qtyText = Number.isFinite(qtyNum) ? String(qtyNum) : t.quantity;
      const headline = `${isBuy ? "B" : "S"} ${qtyText}@${priceText}`;
      // When the fill is on a different contract than the charted one
      // (option-routed backtests: chart = base, fills = options), name the
      // traded contract on its own line so the premium price makes sense.
      const symbolLine =
        t.symbol && instrument?.symbol && t.symbol !== instrument.symbol
          ? `\n${t.symbol}`
          : "";
      const text = isClose
        ? `${headline}\nP/L: ${pnl > 0 ? "+" : ""}${pnl.toFixed(2)}${symbolLine}`
        : `${headline}${symbolLine}`;
      // Border + arrow color tracks side (so direction is unambiguous).
      // Text color tracks P/L for close rows (green profit / red loss)
      // and side for open rows.
      const sideColor = isBuy ? "#059669" : "#dc2626";
      const textColor = isClose
        ? pnl > 0
          ? "#059669"
          : "#dc2626"
        : sideColor;
      const id = chart.createOverlay({
        name: "tradeMarker",
        points: [{ timestamp: anchorTs, value: yValue }],
        // tradeMarker reads .text, .direction and .level from extendData (it
        // can't just pull the side from styles since color isn't enough
        // — the line/arrow geometry needs to flip too). `level` is the
        // collision-avoidance lane computed above.
        extendData: { text, direction: dir, level: lane },
        styles: {
          line: { color: sideColor, style: "dashed" },
          polygon: { color: sideColor },
          text: {
            color: textColor,
            backgroundColor: "#ffffff",
            borderColor: sideColor,
            size: 11,
          },
        },
      });
      if (typeof id === "string") newIds.push(id);
    }
    tradeOverlayIdsRef.current = newIds;
  }, [trades, bars, showTrades, instrument?.symbol]);

  // 6. Listen for Delete / Backspace at document level. If the user has
  //    clicked a drawing (its id is in selectedOverlayIdRef), remove just
  //    that one. Ignore the key if focus is inside an input/textarea so we
  //    don't break text editing in forms (modal-edit indicator params,
  //    etc.). We deliberately do NOT prevent default — Backspace still acts
  //    as Back navigation outside of form fields if the OS does that.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      // Escape dismisses any open context menu first.
      if (e.key === "Escape") {
        setContextMenu(null);
        return;
      }
      if (e.key !== "Delete" && e.key !== "Backspace") return;
      const tag = (e.target as HTMLElement | null)?.tagName ?? "";
      const isEditable =
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        (e.target as HTMLElement | null)?.isContentEditable;
      if (isEditable) return;
      const id = selectedOverlayIdRef.current;
      if (!id) return;
      chartRef.current?.removeOverlay({ id });
      selectedOverlayIdRef.current = null;
      e.preventDefault();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  // Create-overlay helper called from the toolbar. We pass an onClick
  // callback so each user-drawn overlay registers itself as "selected" when
  // tapped — that's what makes Delete / Backspace work on it.
  // Reusable right-click handler attached to every user-drawn overlay.
  // klinecharts injects `preventDefault` onto the event at runtime — the
  // type signature doesn't declare it, but it's there. Calling it stops
  // klinecharts from auto-removing the overlay so the user can pick what
  // to do via our menu.
  // Klinecharts hands us `pageX` / `pageY` — *document-relative*
  // coordinates. Our context menu + popovers use `position: fixed`, which
  // is *viewport-relative*, so on a scrolled page (Backtest detail page
  // scrolls) the menu would land at the bottom of the viewport instead of
  // at the cursor. Convert to viewport coords by subtracting scroll
  // offsets.
  function eventToViewport(event: {
    pageX?: number;
    pageY?: number;
  }): { x: number; y: number } {
    const x =
      typeof event.pageX === "number"
        ? event.pageX - window.scrollX
        : window.innerWidth / 2;
    const y =
      typeof event.pageY === "number"
        ? event.pageY - window.scrollY
        : window.innerHeight / 2;
    return { x, y };
  }

  function makeRightClickHandler() {
    return (event: {
      overlay: { id: string; name: string };
      pageX?: number;
      pageY?: number;
      preventDefault?: () => void;
    }) => {
      event.preventDefault?.();
      const { x, y } = eventToViewport(event);
      setContextMenu({
        overlayId: event.overlay.id,
        overlayName: event.overlay.name,
        x,
        y,
      });
    };
  }

  // Open the volume-profile edit popover prefilled from a placed overlay.
  // Shared by the overlay's onDoubleClick and the right-click context menu's
  // "Edit profile…" entry so both entry points produce the same UX.
  function openVpEdit(args: {
    overlayId: string;
    kind: "volumeProfile" | "weeklyVolumeProfile";
    extendData: unknown;
    x: number;
    y: number;
  }) {
    const ext = (args.extendData ?? {}) as {
      numLevels?: number;
      valueAreaPct?: number;
      period?: "daily" | "weekly" | "monthly" | "quarterly";
      periodCount?: number;
      developing?: boolean;
    };
    setVpEdit({
      overlayId: args.overlayId,
      kind: args.kind,
      x: Math.min(args.x, window.innerWidth - 280),
      y: Math.min(args.y, window.innerHeight - 220),
      numLevels: ext.numLevels ?? 30,
      valueAreaPct: ext.valueAreaPct ?? 0.7,
      period: ext.period ?? "weekly",
      periodCount: ext.periodCount ?? 1,
      developing: ext.developing ?? false,
    });
  }

  // Batch fetcher for the weeklyVolumeProfile overlay. One round trip,
  // one profile per ISO week, all using the same finest-TF the backend
  // picks for the visible window — so the per-week POCs match the
  // manual volumeProfile overlay's data source rather than disagreeing
  // because the client-side fallback was forced onto the chart's TF.
  // Debounced re-fetch of all placed periodic profiles after lazy history
  // loads widen the chart's bar range. Without this, buckets older than the
  // initial ~1000-bar window never receive server profiles ("monthly dPOC
  // stops plotting beyond 2 months on a 15m chart").
  const periodicRefreshTimerRef = useRef<number | null>(null);
  function schedulePeriodicOverlayRefresh(): void {
    if (periodicRefreshTimerRef.current !== null) {
      window.clearTimeout(periodicRefreshTimerRef.current);
    }
    periodicRefreshTimerRef.current = window.setTimeout(() => {
      periodicRefreshTimerRef.current = null;
      const chart = chartRef.current;
      if (!chart) return;
      const overlays = chart.getOverlays({ name: "weeklyVolumeProfile" }) ?? [];
      for (const ov of overlays) {
        if (ov?.id) void fetchAndStashWeeklyProfiles(ov.id);
      }
    }, 600);
  }

  async function fetchAndStashWeeklyProfiles(overlayId: string): Promise<void> {
    const chart = chartRef.current;
    const inst = instrumentRef.current;
    if (!chart || !inst) return;
    const data = chart.getDataList();
    if (data.length === 0) return;
    const startMs = data[0].timestamp;
    const endMs = data[data.length - 1].timestamp + timeframeToMs(timeframe);
    const ext = (chart.getOverlays({ id: overlayId })?.[0]?.extendData ??
      {}) as {
      numLevels?: number;
      period?: "daily" | "weekly" | "monthly" | "quarterly";
      periodCount?: number;
      developing?: boolean;
    };
    try {
      const resp = await getWeeklyVolumeProfiles({
        symbol: inst.symbol,
        exchange: inst.exchange,
        instrument_type: inst.instrument_type,
        expiry: inst.expiry ?? null,
        strike: inst.strike ?? null,
        lot_size: inst.lot_size,
        tick_size: inst.tick_size,
        start: new Date(startMs).toISOString(),
        end: new Date(endMs).toISOString(),
        num_levels: ext.numLevels ?? 30,
        period: ext.period ?? "weekly",
        period_count: ext.periodCount ?? 1,
        developing: ext.developing ?? false,
      });
      if (!chartRef.current?.getOverlays({ id: overlayId })?.[0]) return;
      chartRef.current.overrideOverlay({
        id: overlayId,
        extendData: {
          ...ext,
          serverWeeks: resp.weeks.map((w) => ({
            startMs: new Date(w.start_ts).getTime(),
            endMs: new Date(w.end_ts).getTime(),
            poc: Number(w.poc),
            vah: Number(w.vah),
            val: Number(w.val),
            levels: w.levels.map((lv) => ({
              lo: Number(lv.lo),
              hi: Number(lv.hi),
              volume: Number(lv.volume),
            })),
            developing: (w.developing ?? []).map((d) => ({
              ms: new Date(d.ts).getTime(),
              poc: Number(d.poc),
            })),
          })),
          usedTimeframe: resp.used_timeframe,
        },
      });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[trade1] weeklyVolumeProfile server fetch failed", e);
    }
  }

  // Volume-profile server compute. Reads the overlay's two endpoint
  // timestamps and asks the backend to compute the profile from the *finest*
  // timeframe in the DuckDB store (e.g. 1m bars even when the chart is at
  // 1h). Resulting levels are stashed in extendData.serverProfile and the
  // overlay re-renders from them — that's what makes a profile drawn over
  // a single coarse candle show varying volume across price rows.
  async function fetchAndStashVpProfile(overlayId: string): Promise<void> {
    const chart = chartRef.current;
    const inst = instrumentRef.current;
    if (!chart || !inst) return;
    const overlay = chart.getOverlays({ id: overlayId })?.[0];
    if (!overlay) return;
    const points = overlay.points ?? [];
    if (points.length < 2) return;
    const t0Raw = points[0]?.timestamp;
    const t1Raw = points[1]?.timestamp;
    const tsFromDataIndex = (idx: number | undefined): number | null => {
      if (typeof idx !== "number" || !Number.isFinite(idx)) return null;
      const list = chart.getDataList();
      const i = Math.max(0, Math.min(list.length - 1, idx));
      return list[i]?.timestamp ?? null;
    };
    const t0 =
      typeof t0Raw === "number"
        ? t0Raw
        : tsFromDataIndex(points[0]?.dataIndex);
    const t1 =
      typeof t1Raw === "number"
        ? t1Raw
        : tsFromDataIndex(points[1]?.dataIndex);
    if (t0 == null || t1 == null) return;
    const startMs = Math.min(t0, t1);
    // Each bar's timestamp is the *start* of its period, so the last clicked
    // candle is only fully included if we extend by one chart-TF duration.
    // Without this, drawing a profile over a single 1h candle gives a
    // zero-width [start, end] window — the backend finds at most 1 bar in
    // any TF (15m, 1m, anything), every row in the resulting profile gets
    // the same volume, and the user thinks the fine-TF fallback isn't
    // working.
    const tfMs = timeframeToMs(timeframe);
    const endMs = Math.max(t0, t1) + tfMs;
    if (endMs <= startMs) return;
    const ext = (overlay.extendData ?? {}) as {
      numLevels?: number;
      valueAreaPct?: number;
    };
    try {
      const resp = await getVolumeProfile({
        symbol: inst.symbol,
        exchange: inst.exchange,
        instrument_type: inst.instrument_type,
        expiry: inst.expiry ?? null,
        strike: inst.strike ?? null,
        lot_size: inst.lot_size,
        tick_size: inst.tick_size,
        start: new Date(startMs).toISOString(),
        end: new Date(endMs).toISOString(),
        num_levels: ext.numLevels ?? 30,
        value_area_pct: ext.valueAreaPct ?? 0.7,
        preferred_timeframe: timeframe,
      });
      // Verify the overlay still exists; the user may have removed it
      // during the await.
      const existing = chartRef.current?.getOverlays({ id: overlayId })?.[0];
      if (!existing) return;
      const serverExtend = {
        ...ext,
        serverProfile: {
          poc: Number(resp.poc),
          vah: Number(resp.vah),
          val: Number(resp.val),
          totalVolume: Number(resp.total_volume),
          levels: resp.levels.map((lv) => ({
            lo: Number(lv.lo),
            hi: Number(lv.hi),
            volume: Number(lv.volume),
            mid: (Number(lv.lo) + Number(lv.hi)) / 2,
          })),
        },
        usedTimeframe: resp.used_timeframe,
      };
      // klinecharts v10: overriding only `extendData` updates the model but
      // does NOT repaint the overlay, so the fetched fine-grained profile
      // would never show — invisible over a multi-candle range (the local
      // fallback there is already varied) but leaving a *single* coarse candle
      // stuck on its flat, uniformly-distributed fallback. Remove + recreate
      // (the same tactic the weekly overlay uses) so createPointFigures re-runs
      // with serverProfile present. Preserve the drawn points.
      const pts = existing.points;
      chartRef.current?.removeOverlay({ id: overlayId });
      const newId = createVpOverlay(serverExtend, pts);
      if (selectedOverlayIdRef.current === overlayId) {
        selectedOverlayIdRef.current = newId;
      }
    } catch (e) {
      // Endpoint 404s when no bars; just leave the local fallback in
      // place. eslint-disable-next-line no-console
      console.warn("[trade1] volumeProfile server fetch failed", e);
    }
  }

  // Create a volumeProfile overlay with all its handlers. `points` omitted =
  // interactive draw mode (user clicks two points); `points` provided =
  // recreate an already-drawn profile (used to force a repaint after the
  // server profile is fetched, since v10's overrideOverlay won't repaint).
  function createVpOverlay(
    extendData: Record<string, unknown>,
    points?: unknown,
  ): string | null {
    const chart = chartRef.current;
    if (!chart) return null;
    const created = chart.createOverlay({
      name: "volumeProfile",
      extendData,
      ...(points ? { points: points as never } : {}),
      onClick: (event) => {
        selectedOverlayIdRef.current = event.overlay.id;
      },
      onRightClick: makeRightClickHandler(),
      // Fires after the user's second click finishes drawing. Trigger a
      // server-side recompute so a profile drawn over even a single candle
      // still uses fine-grained underlying bars. (No-op when `points` are
      // supplied — the overlay is already finished, so drawing never starts.)
      onDrawEnd: (event) => {
        void fetchAndStashVpProfile(event.overlay.id);
      },
      // Endpoint drag finished — extent may have grown/shrunk, refetch.
      onPressedMoveEnd: (event) => {
        void fetchAndStashVpProfile(event.overlay.id);
      },
      onDoubleClick: (event) => {
        // pageX/pageY are document-relative — convert to viewport coords
        // for our fixed-positioned popover. Falls back to viewport center
        // if missing on touch devices.
        const { x, y } = eventToViewport(event);
        openVpEdit({
          overlayId: event.overlay.id,
          kind: "volumeProfile",
          extendData: event.overlay.extendData,
          x,
          y,
        });
      },
    });
    return Array.isArray(created) ? (created[0] ?? null) : (created ?? null);
  }

  function spawnOverlay(toolName: string) {
    const chart = chartRef.current;
    if (!chart) return;
    if (toolName === "volumeProfile") {
      // Default params; the user edits these via the double-click popover
      // or the right-click "Edit profile…" entry.
      createVpOverlay({ numLevels: 30, valueAreaPct: 0.7 });
      return;
    }
    if (toolName === "weeklyVolumeProfile") {
      // Auto-place this overlay — it computes from all visible bars, so
      // there's nothing for the user to click on the chart. Provide a
      // dummy point at the first bar; klinecharts treats totalStep=1 as
      // "finalize immediately" once a point exists. The overlay first
      // renders from a client-side fallback (chart-TF bars) so the user
      // sees *something* immediately; meanwhile we fire off a single
      // batch call to /bars/weekly-volume-profiles which fills
      // extendData.serverWeeks with per-week profiles computed from the
      // *finest* TF in the DuckDB store. After it lands the overlay
      // re-renders and matches the manual volumeProfile overlay's POC.
      const data = chart.getDataList();
      if (data.length === 0) return;
      const id = chart.createOverlay({
        name: toolName,
        extendData: {
          numLevels: 30,
          period: "weekly",
          periodCount: 1,
          developing: false,
        },
        points: [{ timestamp: data[0].timestamp, value: data[0].close }],
        onClick: (event) => {
          selectedOverlayIdRef.current = event.overlay.id;
        },
        onRightClick: makeRightClickHandler(),
      });
      const overlayId = Array.isArray(id) ? id[0] : id;
      if (overlayId) void fetchAndStashWeeklyProfiles(overlayId);
      return;
    }
    chart.createOverlay({
      name: toolName,
      onClick: (event) => {
        selectedOverlayIdRef.current = event.overlay.id;
      },
      onRightClick: makeRightClickHandler(),
    });
  }

  function applyVpEdit() {
    if (!vpEdit) return;
    const { overlayId, kind, numLevels, valueAreaPct, period, periodCount, developing } = vpEdit;
    // Invalidate any cached server data so the next render falls back to
    // the local approximation until the fresh server fetch returns.
    // The two overlays use different cache keys (serverProfile vs
    // serverWeeks) so we route by kind.
    if (kind === "volumeProfile") {
      chartRef.current?.overrideOverlay({
        id: overlayId,
        extendData: { numLevels, valueAreaPct, serverProfile: null },
      });
      setVpEdit(null);
      void fetchAndStashVpProfile(overlayId);
    } else {
      // Periodic overlay: switching period changes the bucket count
      // (52 → 12 → 4 columns), the bucket-boundary geometry, and the
      // server cache key. Reusing the existing overlay via
      // overrideOverlay was leaving residue on the canvas — the old
      // weekly columns visually persisted alongside the new monthly
      // ones, which the user reported as "adds another monthly
      // profile". Cleanest fix: remove + recreate so no klinecharts
      // overlay state crosses the period boundary.
      const chart = chartRef.current;
      setVpEdit(null);
      if (!chart) return;
      chart.removeOverlay({ id: overlayId });
      const data = chart.getDataList();
      if (data.length === 0) return;
      const newId = chart.createOverlay({
        name: "weeklyVolumeProfile",
        extendData: { numLevels, period, periodCount, developing },
        points: [{ timestamp: data[0].timestamp, value: data[0].close }],
        onClick: (event) => {
          selectedOverlayIdRef.current = event.overlay.id;
        },
        onRightClick: makeRightClickHandler(),
      });
      const finalId = Array.isArray(newId) ? newId[0] : newId;
      if (finalId) void fetchAndStashWeeklyProfiles(finalId);
    }
  }

  return (
    <div
      className="relative w-full h-full"
      // Suppress the browser's native context menu over the chart so it
      // doesn't cover (or compete with) our custom right-click overlay menu.
      onContextMenu={(e) => e.preventDefault()}
    >
      <DrawingToolbar
        onTool={spawnOverlay}
        onClearAll={() => {
          chartRef.current?.removeOverlay();
          selectedOverlayIdRef.current = null;
        }}
      />
      <div ref={containerRef} className={heightClass ?? "h-[480px]"} />
      <div className="absolute top-3 left-3 z-10 flex items-center gap-2">
        {watermark && (
          <span className="text-sm font-mono text-fg bg-surface border border-border px-2 py-1 rounded shadow-sm pointer-events-none">
            {watermark}
          </span>
        )}
        {timeframe && (
          <span
            className="text-sm font-mono font-bold uppercase text-emerald-800 bg-emerald-50 border border-emerald-400 dark:text-emerald-300 dark:bg-emerald-500/15 dark:border-emerald-500/50 px-2 py-1 rounded shadow-sm pointer-events-none"
            title="Candle duration"
          >
            {timeframe}
          </span>
        )}
        <button
          type="button"
          onClick={() => setJumpOpen((v) => !v)}
          title="Jump to a specific date / time on the chart"
          className="text-xs font-medium text-fg bg-surface border border-border hover:bg-surface-2 px-2 py-1 rounded shadow-sm"
        >
          ⤵ Go to date
        </button>
        {trades.length > 0 && (
          <button
            type="button"
            onClick={() => setShowTrades((v) => !v)}
            title={
              showTrades
                ? "Hide BUY/SELL trade markers"
                : "Show BUY/SELL trade markers"
            }
            className="text-xs font-medium text-fg bg-surface border border-border hover:bg-surface-2 px-2 py-1 rounded shadow-sm"
          >
            {showTrades ? "◎ Hide trades" : "◉ Show trades"}
          </button>
        )}
      </div>
      {jumpOpen && (
        <JumpToDatePopover
          range={barRangeRef.current}
          onCancel={() => setJumpOpen(false)}
          onApply={(ts) => jumpTo(ts)}
        />
      )}
      {indicatorEdit && (
        <IndicatorEditPopover
          edit={indicatorEdit}
          onChange={setIndicatorEdit}
          onCancel={cancelIndicatorEdit}
          onApply={applyIndicatorEdit}
        />
      )}
      {contextMenu && (
        <>
          {/* Invisible full-screen backdrop. Closes on plain left-click
              outside the menu, but we deliberately do NOT close on a
              second right-click — that would have made the menu disappear
              immediately because the same gesture that opened it would
              propagate to the backdrop on the next render. */}
          <div
            className="fixed inset-0 z-30"
            onMouseDown={(e) => {
              // Only dismiss on left mouse-down. button===2 is right-click.
              if (e.button === 0) setContextMenu(null);
            }}
            onContextMenu={(e) => e.preventDefault()}
          />
          <div
            className="fixed z-40 min-w-[140px] bg-surface border border-border rounded shadow-lg py-1 text-xs"
            style={{
              left: Math.min(contextMenu.x, window.innerWidth - 160),
              top: Math.min(contextMenu.y, window.innerHeight - 100),
            }}
            onContextMenu={(e) => e.preventDefault()}
          >
            {(contextMenu.overlayName === "volumeProfile" ||
              contextMenu.overlayName === "weeklyVolumeProfile") && (
              <button
                type="button"
                onClick={() => {
                  const chart = chartRef.current;
                  const found = chart
                    ?.getOverlays({ id: contextMenu.overlayId })
                    ?.[0];
                  openVpEdit({
                    overlayId: contextMenu.overlayId,
                    kind: contextMenu.overlayName as
                      | "volumeProfile"
                      | "weeklyVolumeProfile",
                    extendData: found?.extendData,
                    x: contextMenu.x,
                    y: contextMenu.y,
                  });
                  setContextMenu(null);
                }}
                className="block w-full text-left px-3 py-1.5 text-fg hover:bg-surface-2"
              >
                Edit profile…
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                chartRef.current?.removeOverlay({ id: contextMenu.overlayId });
                if (selectedOverlayIdRef.current === contextMenu.overlayId) {
                  selectedOverlayIdRef.current = null;
                }
                setContextMenu(null);
              }}
              className="block w-full text-left px-3 py-1.5 text-down-600 dark:text-down-400 hover:bg-down-50 dark:hover:bg-down-500/10"
            >
              Remove
            </button>
            <button
              type="button"
              onClick={() => setContextMenu(null)}
              className="block w-full text-left px-3 py-1.5 text-fg hover:bg-surface-2"
            >
              Cancel
            </button>
          </div>
        </>
      )}
      {vpEdit && (
        <div
          className="fixed z-30 w-64 bg-surface border border-border rounded shadow-lg p-3 text-xs"
          style={{
            // Cap to viewport so the popover doesn't get clipped off-screen
            // when the double-click lands near a chart edge.
            left: Math.min(vpEdit.x + 8, window.innerWidth - 280),
            top: Math.min(vpEdit.y + 8, window.innerHeight - 200),
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") applyVpEdit();
            if (e.key === "Escape") setVpEdit(null);
          }}
        >
          <div className="font-medium text-fg mb-2">
            {vpEdit.kind === "weeklyVolumeProfile"
              ? "Periodic volume profile params"
              : "Volume profile params"}
          </div>
          {vpEdit.kind === "weeklyVolumeProfile" && (
            <label className="block mb-2">
              <span className="text-fg-subtle">Period</span>
              <select
                value={vpEdit.period}
                onChange={(e) =>
                  setVpEdit((cur) =>
                    cur
                      ? {
                          ...cur,
                          period: e.target.value as
                            | "daily"
                            | "weekly"
                            | "monthly"
                            | "quarterly",
                        }
                      : cur,
                  )
                }
                className="mt-1 w-full border border-border rounded px-2 py-1 font-mono"
                autoFocus
              >
                <option value="daily">daily</option>
                <option value="weekly">weekly</option>
                <option value="monthly">monthly</option>
                <option value="quarterly">quarterly</option>
              </select>
            </label>
          )}
          {vpEdit.kind === "weeklyVolumeProfile" && (
            <label className="block mb-2">
              <span className="text-fg-subtle">
                Every N {vpEdit.period === "daily"
                  ? "day(s)"
                  : vpEdit.period === "weekly"
                    ? "week(s)"
                    : vpEdit.period === "monthly"
                      ? "month(s)"
                      : "quarter(s)"}
              </span>
              <input
                type="number"
                min={1}
                max={90}
                value={vpEdit.periodCount}
                onChange={(e) =>
                  setVpEdit((cur) =>
                    cur
                      ? {
                          ...cur,
                          periodCount: Math.max(
                            1,
                            Math.min(90, Math.floor(Number(e.target.value) || 1)),
                          ),
                        }
                      : cur,
                  )
                }
                className="mt-1 w-full border border-border rounded px-2 py-1 font-mono"
              />
            </label>
          )}
          {vpEdit.kind === "weeklyVolumeProfile" && (
            <label className="flex items-center gap-2 mb-2">
              <input
                type="checkbox"
                checked={vpEdit.developing}
                onChange={(e) =>
                  setVpEdit((cur) =>
                    cur ? { ...cur, developing: e.target.checked } : cur,
                  )
                }
              />
              <span className="text-fg-subtle">
                Developing POC (running fair-value line)
              </span>
            </label>
          )}
          <label className="block mb-2">
            <span className="text-fg-subtle">Levels (buckets)</span>
            <input
              type="number"
              min={4}
              max={200}
              step={1}
              value={vpEdit.numLevels}
              onChange={(e) =>
                setVpEdit((cur) =>
                  cur
                    ? {
                        ...cur,
                        numLevels: Math.max(
                          4,
                          Math.min(200, Number(e.target.value) || cur.numLevels),
                        ),
                      }
                    : cur,
                )
              }
              className="mt-1 w-full border border-border rounded px-2 py-1 font-mono"
              autoFocus={vpEdit.kind === "volumeProfile"}
            />
          </label>
          {vpEdit.kind === "volumeProfile" && (
            <label className="block mb-3">
              <span className="text-fg-subtle">Value area (%)</span>
              <input
                type="number"
                min={10}
                max={100}
                step={1}
                value={Math.round(vpEdit.valueAreaPct * 100)}
                onChange={(e) =>
                  setVpEdit((cur) =>
                    cur
                      ? {
                          ...cur,
                          valueAreaPct: Math.max(
                            0.1,
                            Math.min(
                              1,
                              (Number(e.target.value) || 70) / 100,
                            ),
                          ),
                        }
                      : cur,
                  )
                }
                className="mt-1 w-full border border-border rounded px-2 py-1 font-mono"
              />
            </label>
          )}
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setVpEdit(null)}
              className="px-2 py-1 text-fg-subtle hover:bg-surface-2 rounded"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={applyVpEdit}
              className="px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
            >
              Apply
            </button>
          </div>
          <div className="text-[10px] text-muted mt-2">
            Tip: drag the start / end anchor circles to move the range.
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Backend serializes datetimes either as naive UTC ("…T00:00:00") or with a
 * TZ suffix (Delta India bars carry "+05:30"). Defensively normalize to a
 * UTC epoch in ms.
 */
/** Price precision by magnitude: FX pairs (~1.1052) need 5 decimals, index /
 *  equity levels read best with 2. */
function fmtPrice(v: number): string {
  if (!Number.isFinite(v)) return "—";
  const decimals = Math.abs(v) < 10 ? 5 : 2;
  return v.toLocaleString("en-IN", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtVolume(v: number): string {
  if (!Number.isFinite(v) || v === 0) return "0";
  return v.toLocaleString("en-IN", {
    notation: "compact",
    maximumFractionDigits: 2,
  });
}


/** Convert our "1m" / "5m" / "1h" / "1d" string to a klinecharts Period. */
function periodFromString(tf: string | undefined): {
  type: "minute" | "hour" | "day";
  span: number;
} {
  if (!tf) return { type: "minute", span: 1 };
  const m = /^(\d+)([mhd])$/.exec(tf);
  if (!m) return { type: "minute", span: 1 };
  const span = Number(m[1]);
  const unit = m[2];
  if (unit === "m") return { type: "minute", span };
  if (unit === "h") return { type: "hour", span };
  return { type: "day", span };
}

/** Bar duration of `tf` in ms — e.g. "1h" -> 3_600_000. Defaults to 1 min
 *  if the string is missing or unparseable so single-candle volume-profile
 *  queries always extend by at least one bar. */
function timeframeToMs(tf: string | undefined): number {
  const p = periodFromString(tf);
  const unitMs =
    p.type === "minute" ? 60_000 : p.type === "hour" ? 3_600_000 : 86_400_000;
  return p.span * unitMs;
}

// ---------------------------------------------------------------------------
// Jump-to-date popover — pick a date/time and scroll the time axis there.
// ---------------------------------------------------------------------------

function JumpToDatePopover({
  range,
  onCancel,
  onApply,
}: {
  range: { min: number; max: number } | null;
  onCancel: () => void;
  onApply: (ts: number) => void;
}) {
  // Default to the midpoint of the loaded range so the picker is initialized
  // somewhere useful even on long historical windows.
  const defaultMs = range ? Math.floor((range.min + range.max) / 2) : Date.now();
  const [value, setValue] = useState(toLocalInputValue(defaultMs));
  const minAttr = range ? toLocalInputValue(range.min) : undefined;
  const maxAttr = range ? toLocalInputValue(range.max) : undefined;
  return (
    <div
      className="absolute top-14 left-3 z-20 w-64 bg-surface border border-border rounded shadow-lg p-3 space-y-2 text-xs"
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          const ts = parseLocalInputValue(value);
          if (Number.isFinite(ts)) onApply(ts);
        }
        if (e.key === "Escape") onCancel();
      }}
    >
      <div className="font-medium text-fg">Jump to date / time</div>
      <input
        type="datetime-local"
        value={value}
        min={minAttr}
        max={maxAttr}
        onChange={(e) => setValue(e.target.value)}
        className="w-full border border-border rounded px-2 py-1 font-mono"
        autoFocus
      />
      {range && (
        <div className="text-[10px] text-muted">
          Loaded range:{" "}
          <span className="font-mono">
            {new Date(range.min).toLocaleDateString()} →{" "}
            {new Date(range.max).toLocaleDateString()}
          </span>
        </div>
      )}
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-2 py-1 text-fg-subtle hover:bg-surface-2 rounded"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => {
            const ts = parseLocalInputValue(value);
            if (Number.isFinite(ts)) onApply(ts);
          }}
          className="px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
        >
          Go
        </button>
      </div>
    </div>
  );
}

/** "YYYY-MM-DDTHH:mm" in local time for the datetime-local input. */
function toLocalInputValue(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}

function parseLocalInputValue(v: string): number {
  if (!v) return NaN;
  // datetime-local values have no tz; new Date(v) treats them as local time,
  // which is what we want.
  return new Date(v).getTime();
}

// ---------------------------------------------------------------------------
// Indicator edit popover — change calcParams, color, line width.
// ---------------------------------------------------------------------------

type IndicatorEditState = {
  klineId: string;
  instanceId: string;
  type: import("../lib/indicators").IndicatorType;
  params: Record<string, number>;
  color: string;
  lineWidth: number;
  label: string;
};

function IndicatorEditPopover({
  edit,
  onChange,
  onCancel,
  onApply,
}: {
  edit: IndicatorEditState;
  onChange: (next: IndicatorEditState) => void;
  onCancel: () => void;
  onApply: () => void;
}) {
  return (
    <div
      className="absolute top-14 right-3 z-20 w-72 bg-surface border border-border rounded shadow-lg p-3 space-y-2 text-xs"
      onKeyDown={(e) => {
        if (e.key === "Enter") onApply();
        if (e.key === "Escape") onCancel();
      }}
    >
      <div className="font-medium text-fg">Edit {edit.label}</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {Object.entries(edit.params).map(([k, v]) => (
          <label key={k} className="block">
            <span className="text-fg-subtle">{k}</span>
            <input
              type="number"
              value={v}
              min={k === "multiplier" ? 0.1 : 1}
              step={k === "std_dev" || k === "multiplier" ? 0.1 : 1}
              onChange={(e) =>
                onChange({
                  ...edit,
                  params: {
                    ...edit.params,
                    [k]: Math.max(
                      k === "multiplier" ? 0.1 : 1,
                      Number(e.target.value) || edit.params[k],
                    ),
                  },
                })
              }
              className="mt-1 w-full border border-border rounded px-2 py-1 font-mono"
            />
          </label>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <label className="block">
          <span className="text-fg-subtle">Color</span>
          <input
            type="color"
            value={edit.color}
            onChange={(e) => onChange({ ...edit, color: e.target.value })}
            className="mt-1 w-full h-7 border border-border rounded"
          />
        </label>
        <label className="block">
          <span className="text-fg-subtle">Line width (px)</span>
          <input
            type="number"
            min={1}
            max={6}
            step={1}
            value={edit.lineWidth}
            onChange={(e) =>
              onChange({
                ...edit,
                lineWidth: Math.max(
                  1,
                  Math.min(6, Number(e.target.value) || 1),
                ),
              })
            }
            className="mt-1 w-full border border-border rounded px-2 py-1 font-mono"
          />
        </label>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onCancel}
          className="px-2 py-1 text-fg-subtle hover:bg-surface-2 rounded"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={onApply}
          className="px-3 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700"
        >
          Apply
        </button>
      </div>
    </div>
  );
}
